// Is this tab running the latest deployed build?
//
// CRA emits hashed bundles (static/js/main.<hash>.js) and an
// asset-manifest.json that names the current one. The running page knows
// which main.*.js it loaded; fetching the manifest fresh (no-store + a
// cache-busting query, so neither the browser nor the CDN answers from
// cache) tells us which one is live. Different hash = a deploy happened
// since this tab loaded = the user is on a stale UI and should reload.
//
// Checked on load (initial visit), whenever the tab becomes visible again
// after being hidden a while, and on an interval while open. Result is kept
// in module state so the UI notice (components/BuildFreshness.js) and the
// NoVA screen outline (utils/screenOutline.js) read the same answer.
// Same file in the control plane and the App Kit foundation — edit both.

const state = { current: '', live: '', stale: false, checkedAt: 0, error: '' };
const listeners = new Set();
const notify = () => { for (const fn of listeners) { try { fn(state); } catch { /* listener */ } } };

const basename = (p) => String(p || '').split('?')[0].split('/').pop();

export const getBuildFreshness = () => state;
export function subscribeBuildFreshness(fn) { listeners.add(fn); return () => listeners.delete(fn); }

// The main bundle this page loaded (empty in dev / non-CRA hosts).
export function currentBuild() {
  if (typeof document === 'undefined') return '';
  const s = Array.from(document.querySelectorAll('script[src]')).find((el) => /\/static\/js\/main\.[\w-]+\.js/.test(el.src));
  return s ? basename(s.src) : '';
}

export async function fetchLiveBuild() {
  const r = await fetch(`/asset-manifest.json?t=${Date.now()}`, { cache: 'no-store', credentials: 'same-origin' });
  if (!r.ok) throw new Error(`asset-manifest ${r.status}`);
  const j = await r.json();
  return basename(j?.files?.['main.js'] || '');
}

export async function checkBuildFresh() {
  const current = currentBuild();
  if (!current) return state; // dev server / native shell / non-CRA page
  try {
    const live = await fetchLiveBuild();
    Object.assign(state, { current, live, stale: !!live && live !== current, checkedAt: Date.now(), error: '' });
  } catch (err) {
    Object.assign(state, { current, checkedAt: Date.now(), error: String(err?.message || err) });
  }
  notify();
  return state;
}

// Start the watch; returns stop(). `everyMs`: periodic check while the tab
// is open (a browser left open for days still learns about deploys).
// `hiddenMs`: a tab coming back after at least this long hidden re-checks
// immediately (the "I came back to my laptop" case).
export function startBuildFreshnessWatch({ everyMs = 15 * 60 * 1000, hiddenMs = 5 * 60 * 1000 } = {}) {
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'production' || !currentBuild()) return () => {};
  let hiddenAt = 0;
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
    if (hiddenAt && Date.now() - hiddenAt >= hiddenMs) checkBuildFresh();
    hiddenAt = 0;
  };
  const first = setTimeout(checkBuildFresh, 3000); // after the page settles
  const timer = setInterval(checkBuildFresh, everyMs);
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    clearTimeout(first);
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

// Reload onto the live build. A plain reload revalidates the document
// (no-cache HTML) and the new index points at the new hashed bundles — the
// user does not need to know what a hard refresh is.
export function reloadToLatest() {
  try { window.location.reload(); } catch { /* ignore */ }
}
